// frontend/src/pages/VerificationPage.js
//
// Public page that proves the business behind this website is registered.
// Built to satisfy Termii's DND verification: it shows the CAC business name,
// registration number and the registered address, and lets anyone open the
// certificate itself. The address text is copied EXACTLY as it appears on the
// certificate, because that is what a reviewer matches against.

import React from 'react';
import { FaCheckCircle, FaFilePdf, FaMapMarkerAlt, FaExternalLinkAlt } from 'react-icons/fa';

// The certificate sits in /public, so it is served straight from the site root.
const CERTIFICATE_URL = '/cac-certificate.pdf';

// Everything below is taken verbatim from the CAC certificate.
const BUSINESS = {
  name: 'ESEKIE DIGITAL SERVICES',
  regNumber: '9182646',
  nature: 'Bulk SMS Services',
  address: 'NO. 2, HARRISON IDOKPA COMMUNITY, BENIN, EDO STATE, NIGERIA',
  tin: '2621202684750',
  registeredOn: '8th January, 2026',
  authority: 'Corporate Affairs Commission (CAC), Federal Republic of Nigeria',
};

function VerificationPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Heading */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            <FaCheckCircle /> Registered Business
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Business Verification</h1>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
            Everything Automotive is operated by a business registered with the
            Corporate Affairs Commission of the Federal Republic of Nigeria.
            The registration details and certificate are shown below.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Registration details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Registration Details</h2>
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="text-gray-500">Registered Business Name</dt>
                <dd className="text-gray-900 font-semibold">{BUSINESS.name}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Business Name Registration No.</dt>
                <dd className="text-gray-900 font-semibold">{BUSINESS.regNumber}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Nature of Business</dt>
                <dd className="text-gray-900 font-semibold">{BUSINESS.nature}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Tax Identification Number (TIN)</dt>
                <dd className="text-gray-900 font-semibold">{BUSINESS.tin}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Registered On</dt>
                <dd className="text-gray-900 font-semibold">{BUSINESS.registeredOn}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Registering Authority</dt>
                <dd className="text-gray-900 font-semibold">{BUSINESS.authority}</dd>
              </div>
            </dl>
          </div>

          {/* Registered address */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaMapMarkerAlt className="text-red-500" /> Registered Address
            </h2>
            <p className="text-gray-900 font-semibold text-lg leading-relaxed">
              {BUSINESS.address}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              This is the principal place of business as recorded on the
              certificate of registration.
            </p>

            <div className="mt-auto pt-6">
              <a
                href={CERTIFICATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-lg transition-colors"
              >
                <FaFilePdf /> View Certificate <FaExternalLinkAlt className="text-xs" />
              </a>
            </div>
          </div>
        </div>

        {/* Embedded certificate, visible on the page itself */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Certificate of Registration</h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden" style={{ height: '80vh' }}>
            <object
              data={CERTIFICATE_URL}
              type="application/pdf"
              width="100%"
              height="100%"
              aria-label="Certificate of Registration"
            >
              {/* Shown only if the browser cannot display PDFs inline */}
              <div className="p-8 text-center">
                <FaFilePdf className="text-5xl text-red-500 mx-auto mb-4" />
                <p className="text-gray-700 mb-4">
                  Your browser can't display the certificate here.
                </p>
                <a
                  href={CERTIFICATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-lg"
                >
                  <FaFilePdf /> Open the Certificate
                </a>
              </div>
            </object>
          </div>
        </div>

      </div>
    </div>
  );
}

export default VerificationPage;
